/**
 * runQaVocabulary — the one place the Agent Run QA / Decision Inspector's words live.
 *
 * The verdicts, finding codes, decision-input kinds, statuses and closure reasons are all decided by
 * the backend projection (`app/services/run_qa_inspector.py`, MASTER_FLOW §5A.4). That module
 * deliberately contains no operator-facing prose in any language: it reports identifiers and the
 * values it observed. This module is therefore the single author of every sentence the operator
 * reads, and `runQaVocabulary.parity.test.js` reads the Python source and fails if the server can
 * report an arm this file has no word for.
 *
 * Two failure modes it exists to prevent, both of which this repo has already shipped once:
 *
 *   - A raw identifier on a Hebrew screen. `map[value] || value` is not a fallback, it is the
 *     producer's own English snake_case token rendered as the verdict. Every lookup here goes
 *     through `hasOwnProperty` and answers with a NAMED unknown that claims nothing either way.
 *   - A reassuring face on an unknown arm. The unknown verdict is neutral grey and says "unclassified
 *     — check by hand"; it never borrows the green of `auto_close_eligible`.
 *
 * The action types, run statuses, ticket statuses and evidence sources named inside these sentences
 * are worded by the shared vocabulary modules (`actionTypeVocabulary`, `runStatusVocabulary`,
 * `ticketStatusVocabulary`, `evidenceSourceVocabulary`), never by a private copy here — a second map
 * is how the same value comes to read two ways on two screens.
 */
import { actionTypeLabel } from "./actionTypeVocabulary";
import { runStatusLabel } from "./runStatusVocabulary";
import { ticketStatusLabel } from "./ticketStatusVocabulary";
import { riskGradeLabel } from "./riskGradeVocabulary";
import { EVIDENCE_SOURCE_LABEL } from "./evidenceSourceVocabulary";

// ── verdicts ─────────────────────────────────────────────────────────────────
//
// Strictness is the server's ordering (security first, then §5A.4's own listing). Colour follows it
// so the loudest finding is the loudest thing on the screen.

export const QA_VERDICT_META = {
  security_escalation: {
    label: "הסלמה ביטחונית",
    cls: "bg-red-100 text-red-800 border-red-300",
    hint: "הריצה נגעה בגבול לקוח או במשטח הרשאות/auth — יש לטפל לפני כל דבר אחר.",
    alarm: true,
  },
  file_product_bug: {
    label: "חשד לתקלת מוצר",
    cls: "bg-rose-100 text-rose-800 border-rose-300",
    hint: "הריצה דיווחה על פעולה שלא בוצעה כפי שתוארה — יש לפתוח בירור מוצר.",
    alarm: true,
  },
  priority_review: {
    label: "בירור בעדיפות",
    cls: "bg-orange-100 text-orange-800 border-orange-300",
    hint: "שני מקורות רשומים חלוקים, או שהריצה חזרה על עצמה — יש לבדוק את הראיות.",
    alarm: false,
  },
  human_review: {
    label: "נדרשת סקירה אנושית",
    cls: "bg-amber-100 text-amber-800 border-amber-300",
    hint: "הראיות הרשומות אינן מספיקות כדי להשאיר את התוצאה כפי שהיא.",
    alarm: false,
  },
  auto_close_eligible: {
    label: "כשיר לסגירה אוטומטית",
    cls: "bg-green-100 text-green-700 border-green-200",
    hint: "הריצה הסתיימה נקי, כל קלטי ההחלטה נרשמו, ואין ממצא — אין צורך לקרוא שוב.",
    alarm: false,
  },
  continue_diagnostic: {
    label: "ממשיך לפעול",
    cls: "bg-blue-100 text-blue-700 border-blue-200",
    hint: "הריצה עדיין מתקדמת או ממתינה לפעולה חיצונית — אין מה לאשר כרגע.",
    alarm: false,
  },
  no_action_required: {
    label: "אין צורך בפעולה",
    cls: "bg-gray-200 text-gray-600 border-gray-300",
    hint: "הריצה הוכרעה — אדם כבר סגר אותה, או שהקריאה כבר טופלה.",
    alarm: false,
  },
};

// A verdict this build does not know claims nothing. It must never be dressed as reassurance -- and
// it is an `alarm`, because "the server reported a classification this build cannot read" is a fact
// an operator has to act on, not a quiet grey chip.
export const QA_VERDICT_UNKNOWN_META = {
  label: "ממצא QA לא מזוהה — יש לבדוק ידנית",
  cls: "bg-gray-200 text-gray-700 border-gray-400",
  hint: "השרת דיווח על סיווג שאינו מוכר לגרסה זו של הממשק. אין להסיק ממנו דבר.",
  alarm: true,
};

export function qaVerdictMeta(verdict) {
  return Object.prototype.hasOwnProperty.call(QA_VERDICT_META, verdict)
    ? QA_VERDICT_META[verdict]
    : QA_VERDICT_UNKNOWN_META;
}

// The server's own reason code for the verdict. One sentence each, no interpolation: the code is
// closed, and a code with no sentence here is a word the operator would otherwise never see.
export const QA_VERDICT_REASON_TEXT = {
  findings_forced_verdict: "הממצאים שסומנו הם שקבעו את הסיווג הזה.",
  run_awaiting_human: "הריצה חסומה וממתינה לאדם — סקירה או אישור.",
  run_in_progress: "הריצה עדיין מתקדמת ואינה דורשת התערבות.",
  run_approved_pending_execution: "הפעולה אושרה וטרם בוצעה.",
  run_failed: "הריצה הסתיימה בכשל.",
  run_settled_by_human: "הריצה נדחתה — מצב סופי שאדם כבר הכריע בו.",
  run_paused: "הריצה מושהית לפני שהחלה.",
  closure_clean: "הריצה הסתיימה נקי וכל הראיות הנדרשות נרשמו.",
  closure_blocked: "הריצה הסתיימה אך הראיות אינן תומכות בסגירה ללא סקירה.",
  ticket_already_closed: "הקריאה כבר סגורה או מוזגה — אין מה לסגור.",
  run_status_unclassified: "מצב הריצה אינו מוכר לגרסה זו של הממשק — אין לסווג אותו אוטומטית.",
};

export const QA_VERDICT_REASON_UNKNOWN_TEXT =
  "סיבת הסיווג אינה מוכרת לגרסה זו של הממשק — יש לפתוח את הפירוט שלמטה.";

export function qaVerdictReasonText(reason) {
  return Object.prototype.hasOwnProperty.call(QA_VERDICT_REASON_TEXT, reason)
    ? QA_VERDICT_REASON_TEXT[reason]
    : QA_VERDICT_REASON_UNKNOWN_TEXT;
}

// ── decision-input kinds ─────────────────────────────────────────────────────

export const QA_SLOT_LABEL = {
  employee_scope: "הרשאת עובד מול לקוח",
  autonomy_mode: "מצב אוטונומיה",
  tool_entitlement: "זכאות לכלי (מדיניות)",
  proposed_action: "הפעולה שהוצעה",
  approval_binding: "אישור מקושר",
  execution_result: "מה שבוצע בפועל",
  declared_evidence: "מקורות ראיה שהוצהרו",
  closure_state: "מצב הקריאה",
};

export const QA_SLOT_UNKNOWN_LABEL = "קלט החלטה לא מזוהה";

export function qaSlotLabel(kind) {
  return Object.prototype.hasOwnProperty.call(QA_SLOT_LABEL, kind) ? QA_SLOT_LABEL[kind] : QA_SLOT_UNKNOWN_LABEL;
}

export const QA_SLOT_STATUS_META = {
  recorded: { label: "נרשם", cls: "bg-green-100 text-green-700 border-green-200" },
  reported: { label: "נגזר מרשומות", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  not_provided: { label: "לא נרשם", cls: "bg-gray-200 text-gray-600 border-gray-300" },
  unresolved: { label: "לא ניתן לאמת", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  contradicted: { label: "סותר", cls: "bg-red-100 text-red-700 border-red-200" },
};

// A status this build does not know is not "recorded" and not "fine".
export const QA_SLOT_STATUS_UNKNOWN_META = {
  label: "מצב קלט לא מזוהה",
  cls: "bg-gray-200 text-gray-700 border-gray-400",
};

export function qaSlotStatusMeta(status) {
  return Object.prototype.hasOwnProperty.call(QA_SLOT_STATUS_META, status)
    ? QA_SLOT_STATUS_META[status]
    : QA_SLOT_STATUS_UNKNOWN_META;
}

// ── closure ──────────────────────────────────────────────────────────────────

export const QA_CLOSURE_REASON_TEXT = {
  complete_clean_terminal_run: "כל קלטי ההחלטה הנדרשים נרשמו, הפעולה בוצעה בהצלחה ואין ממצא.",
  run_not_terminal: "הריצה טרם הסתיימה.",
  evidence_incomplete: "חסרים קלטי החלטה הנדרשים כדי להסתמך על התוצאה.",
  findings_present: "סומנו ממצאים — אין להסתמך על התוצאה ללא סקירה.",
  analysis_degraded: "הריצה עצמה סימנה שהניתוח שלה חלקי.",
  ticket_already_closed: "הקריאה כבר סגורה או מוזגה.",
};

export const QA_CLOSURE_REASON_UNKNOWN_TEXT =
  "סיבת אי-הכשירות לסגירה אינה מוכרת לגרסה זו של הממשק.";

export function qaClosureReasonText(reason) {
  return Object.prototype.hasOwnProperty.call(QA_CLOSURE_REASON_TEXT, reason)
    ? QA_CLOSURE_REASON_TEXT[reason]
    : QA_CLOSURE_REASON_UNKNOWN_TEXT;
}

// ── findings ─────────────────────────────────────────────────────────────────
//
// One entry per code the projection can report. `detail` composes the values the server attached;
// it never restates the code, so a finding cannot say the same thing twice.

const action = (token) => (token ? actionTypeLabel(token) : "—");
const status = (value) => (value ? runStatusLabel(value) : "—");
const ticket = (value) => (value ? ticketStatusLabel(value) : "—");
// A count the server did not attach renders as absent. `\`${undefined} פעמים\`` would put the word
// "undefined" inside a Hebrew sentence, which reads as a real value.
const count = (value) => (typeof value === "number" ? String(value) : "—");

export const QA_FINDING_META = {
  action_result_mismatch: {
    title: "הפעולה שדווחה אינה הפעולה שבוצעה",
    detail: (v) => `הניתוח הציע ${action(v.claimed_action_type)}, הרשומה של הביצוע מציינת ${action(v.executed_action_type)}.`,
  },
  silent_tool_failure: {
    title: "הריצה מדווחת הצלחה אך הביצוע אינו שלם",
    detail: (v) =>
      `מצב הריצה ${status(v.run_status)}; executed=${v.executed ? "כן" : "לא"}, success=${v.success ? "כן" : "לא"}.`,
  },
  unresolved_despite_confident_answer: {
    title: "הריצה הכריזה על סיום בלי לסגור את הקריאה",
    detail: (v) => `הפעולה ${action(v.claimed_action_type)} לא בוצעה, והקריאה נותרה ${ticket(v.ticket_status)}.`,
  },
  out_of_scope_action: {
    title: "פעולה מחוץ לגבול הלקוח או למדיניות",
    detail: (v) =>
      v.reason === "employee_ticket_scope_mismatch"
        ? "העובד משויך ללקוח אחר מזה של הקריאה — אותו כלל שמונע יצירת ריצה חדשה לקריאה זו."
        : `המדיניות אוסרת את ${action(v.executed_action_type)} עבור העובד הזה.`,
  },
  contradictory_evidence: {
    title: "שני מקורות רשומים חלוקים",
    detail: (v) =>
      v.reason === "reported_external_effect_without_receipt"
        ? `הביצוע דיווח ש${action(v.executed_action_type)} נשלח ללקוח, אך אין רשומת אפקט קבועה שתתעד את היציאה מהמערכת.`
        : `מקור "${sourceName(v.left_source)}" רושם ${action(v.left_action_type)}, ומקור "${sourceName(v.right_source)}" רושם ${action(v.right_action_type)}.`,
  },
  repeated_loop_or_stall: {
    title: "חזרתיות או תקיעה",
    detail: (v) =>
      v.reason === "tool_call_threshold_exceeded"
        ? `הריצה ביצעה ${count(v.tool_calls)} קריאות כלי (סף ${count(v.threshold)}) ועדיין במצב ${status(v.run_status)}.`
        : `אותה פעולה נרשמה ${count(v.threshold)} פעמים ומעלה: ${repeatSummary(v.repeated_actions)}.`,
  },
  insufficient_evidence_for_closure: {
    title: "אין די ראיות כדי להסתמך על התוצאה",
    detail: (v) => `קלטי החלטה חסרים: ${slotList(v.missing_slots)}.`,
  },
  security_sensitive_trace: {
    title: "פעולה רגישה בוצעה בלי אישור מקושר",
    detail: (v) => `${action(v.executed_action_type)} נוגעת בהרשאות או בהתחברות, ולא נמצא לה טוקן אישור.`,
  },
  unbound_state_change: {
    title: "שינוי מצב בוצע בלי אישור מקושר",
    detail: (v) => `${action(v.executed_action_type)} שינתה מצב בפועל, ולא נמצא לה טוקן אישור.`,
  },
  run_failed: {
    title: "הריצה הסתיימה בכשל",
    detail: (v) => (v.result_summary_present ? "נרשם סיכום תוצאה." : "לא נרשם סיכום תוצאה."),
  },
};

export const QA_FINDING_UNKNOWN_TITLE = "ממצא QA לא מזוהה";

// The name of a recorded source inside a disagreement. These are the projection's own slot names,
// so they are worded by the slot map rather than by a second list here.
const sourceName = (key) => {
  const map = { token: "approval_binding", approved: "approval_binding", executed: "execution_result" };
  const kind = map[key];
  if (!kind) return key || "—";
  return `${qaSlotLabel(kind)}${key === "token" ? " (טוקן)" : ""}`;
};

const repeatSummary = (repeated) => {
  if (!repeated || typeof repeated !== "object") return "—";
  return Object.entries(repeated)
    .map(([token, count]) => `${action(token)} ×${count}`)
    .join(" · ");
};

const slotList = (slots) =>
  Array.isArray(slots) && slots.length ? slots.map((kind) => qaSlotLabel(kind)).join(" · ") : "—";

export function qaFindingMeta(finding) {
  const code = finding && typeof finding.code === "string" ? finding.code : null;
  const values = finding && finding.values && typeof finding.values === "object" ? finding.values : {};
  if (code && Object.prototype.hasOwnProperty.call(QA_FINDING_META, code)) {
    const meta = QA_FINDING_META[code];
    return { ...meta, detail: meta.detail(values) };
  }
  // An unrecognised code renders its own token in a monospace echo and claims nothing more: the
  // operator can see that something was reported even though this build cannot word it.
  return {
    title: QA_FINDING_UNKNOWN_TITLE,
    detail: "קוד הממצא אינו מוכר לגרסה זו של הממשק — ראו את הקוד המוצג.",
    unknown: true,
  };
}

// ── the agent-policy scales the slots quote ──────────────────────────────────
//
// `run.autonomy_mode` is written on `AgentAutonomyLevel`'s scale and `agent.permission_tier` on
// `AgentPermissionTier`'s. `EmployeeRosterCard` words the agent's own copies of these two fields
// inline; the arms are pinned to the backend enums by the parity test rather than to that file, so a
// renamed arm reddens this test instead of quietly falling back to the raw token.

export const QA_AUTONOMY_LABEL = {
  approval_required: "אישור נדרש לכל פעולה",
  supervised: "מפוקח — כתיבה באישור",
  semi_autonomous: "חצי אוטונומי",
  autonomous: "אוטונומי",
};

export const QA_AUTONOMY_UNKNOWN_LABEL = "מצב אוטונומיה לא מזוהה";

export const qaAutonomyLabel = (value) =>
  Object.prototype.hasOwnProperty.call(QA_AUTONOMY_LABEL, value) ? QA_AUTONOMY_LABEL[value] : QA_AUTONOMY_UNKNOWN_LABEL;

export const QA_TIER_LABEL = {
  tier_1: "T1 · עובד קריאה",
  tier_2: "T2 · עובד תפעולי",
  tier_3: "T3 · עובד מוגבה",
};

export const QA_TIER_UNKNOWN_LABEL = "דרגת הרשאה לא מזוהה";

export const qaTierLabel = (value) =>
  Object.prototype.hasOwnProperty.call(QA_TIER_LABEL, value) ? QA_TIER_LABEL[value] : QA_TIER_UNKNOWN_LABEL;

// `ActionRiskLevel` — the analysis's own green/amber/red arm, which is a DIFFERENT scale from the
// 0–3 token grade `riskGradeVocabulary` words. Both appear on this projection under the same key
// name, on two different slots, which is why a value is worded per (slot, key) and never per key.
export const QA_ANALYSIS_RISK_LABEL = {
  green: "נמוך",
  amber: "בינוני",
  red: "גבוה",
};

export const QA_ANALYSIS_RISK_UNKNOWN_LABEL = "רמת סיכון לא מזוהה";

export const qaAnalysisRiskLabel = (value) =>
  Object.prototype.hasOwnProperty.call(QA_ANALYSIS_RISK_LABEL, value)
    ? QA_ANALYSIS_RISK_LABEL[value]
    : QA_ANALYSIS_RISK_UNKNOWN_LABEL;

// `ActionOutcome` — the run's own action rows, counted by outcome.
export const QA_ACTION_OUTCOME_LABEL = {
  proposed: "הוצע",
  approved: "אושר",
  rejected: "נדחה",
  succeeded: "הצליח",
  failed: "נכשל",
};

export const QA_ACTION_OUTCOME_UNKNOWN_LABEL = "תוצאת פעולה לא מזוהה";

export const qaActionOutcomeLabel = (value) =>
  Object.prototype.hasOwnProperty.call(QA_ACTION_OUTCOME_LABEL, value)
    ? QA_ACTION_OUTCOME_LABEL[value]
    : QA_ACTION_OUTCOME_UNKNOWN_LABEL;

export const QA_YES = "כן";
export const QA_NO = "לא";
export const QA_ABSENT = "—";

// ── the fields each decision input may display ───────────────────────────────
//
// One label per (slot kind, field). The parity test extracts every `"key":` from each backend slot
// builder and fails if it has no label here, so a field added to the server's `value`/`detail` dict
// is a red test rather than a row that renders without a name. The labels are per slot, not global,
// because `risk_level` carries the analysis's green/amber/red on `proposed_action` and the 0–3 token
// grade on `approval_binding` — a single global label would name one of them wrongly.

export const QA_SLOT_FIELD_LABEL = {
  employee_scope: {
    agent_client_id: "לקוח של העובד",
    ticket_company_id: "לקוח של הקריאה",
    global_agent: "עובד גלובלי (ללא לקוח מוגבל)",
    scope_mismatch: "חריגה מגבול הלקוח",
  },
  autonomy_mode: {
    autonomy_mode: "מצב אוטונומיה",
    analysis_degraded: "הניתוח סומן חלקי",
  },
  tool_entitlement: {
    subject_action_type: "הפעולה שנבדקה",
    requested_action_type: "התבקשה בניתוח",
    approved_action_type: "אושרה",
    executed_action_type: "בוצעה בפועל",
    permission_tier: "דרגת ההרשאה של העובד",
    entitled: "המדיניות מתירה",
    requires_approval: "המדיניות דורשת אישור",
    forbidden: "המדיניות אוסרת",
    policy_reason: "הודעת המדיניות",
    crisis_mode_read: "מצב חירום נלקח בחשבון",
  },
  proposed_action: {
    action_type: "הפעולה המוצעת",
    risk_level: "רמת הסיכון שהניתוח הצהיר",
    confidence: "ביטחון הניתוח",
    connection_required: "נדרש חיבור",
    analysis_action_types: "פעולות שנרשמו בריצה עצמה",
  },
  approval_binding: {
    token_action_type: "הפעולה שהטוקן קושר אליה",
    approver_id: "מאשר (מזהה)",
    approved_at: "אושר בתאריך",
    used: "נוצל",
    revoked: "בוטל",
    risk_level: "דרגת הסיכון של האישור",
    context_approved_action_type: "הפעולה שנרשמה במרכז האישורים",
    executed_action_type: "בוצעה בפועל",
  },
  execution_result: {
    action_type: "הפעולה שבוצעה",
    executed: "המבצע הופעל",
    success: "המבצע החזיר הצלחה",
    message: "הודעת המבצע",
    action_outcomes: "פעולות הריצה לפי תוצאה",
  },
  declared_evidence: {
    evidence_sources: "מקורות ראיה שהוצהרו",
    missing_information_count: "פריטי מידע שהריצה סימנה כחסרים",
  },
  closure_state: {
    ticket_status: "מצב הקריאה",
    ticket_settled: "הקריאה כבר הוכרעה",
    analysis_degraded: "הניתוח סומן חלקי",
  },
};

export const QA_SLOT_FIELD_UNKNOWN_LABEL = "שדה לא מזוהה";

// What an unnamed field's VALUE is replaced with. A field this build has no label for is a field
// this build does not know the shape of, and rendering it anyway would dump whatever the server
// happens to put there onto the screen — including, one day, a credential. The row still appears, so
// the operator can see that something was reported and that this build could not read it.
export const QA_SLOT_FIELD_UNKNOWN_VALUE = "לא מוצג";

export function qaFieldKnown(slotKind, key) {
  const fields = Object.prototype.hasOwnProperty.call(QA_SLOT_FIELD_LABEL, slotKind)
    ? QA_SLOT_FIELD_LABEL[slotKind]
    : null;
  return Boolean(fields) && Object.prototype.hasOwnProperty.call(fields, key);
}

export function qaFieldLabel(slotKind, key) {
  return qaFieldKnown(slotKind, key) ? QA_SLOT_FIELD_LABEL[slotKind][key] : QA_SLOT_FIELD_UNKNOWN_LABEL;
}

// Value worders, keyed by (slot kind, field). Anything not listed here is echoed as a machine token
// rather than passed through a map that does not hold its scale: a wrong word is worse than an
// obvious identifier, and `riskGradeLabel`/`qaAnalysisRiskLabel` on the wrong slot is exactly how
// the same key comes to read two ways.
const VALUE_WORDERS = {
  "employee_scope.global_agent": (v) => (v ? QA_YES : QA_NO),
  "employee_scope.scope_mismatch": (v) => (v ? QA_YES : QA_NO),
  "autonomy_mode.autonomy_mode": qaAutonomyLabel,
  "autonomy_mode.analysis_degraded": (v) => (v ? QA_YES : QA_NO),
  "tool_entitlement.subject_action_type": actionTypeLabel,
  "tool_entitlement.requested_action_type": actionTypeLabel,
  "tool_entitlement.approved_action_type": actionTypeLabel,
  "tool_entitlement.executed_action_type": actionTypeLabel,
  "tool_entitlement.permission_tier": qaTierLabel,
  "tool_entitlement.entitled": (v) => (v ? QA_YES : QA_NO),
  "tool_entitlement.requires_approval": (v) => (v ? QA_YES : QA_NO),
  "tool_entitlement.forbidden": (v) => (v ? QA_YES : QA_NO),
  "tool_entitlement.crisis_mode_read": (v) => (v ? QA_YES : QA_NO),
  "proposed_action.action_type": actionTypeLabel,
  "proposed_action.risk_level": qaAnalysisRiskLabel,
  "proposed_action.connection_required": (v) => (v ? QA_YES : QA_NO),
  "approval_binding.token_action_type": actionTypeLabel,
  "approval_binding.context_approved_action_type": actionTypeLabel,
  "approval_binding.executed_action_type": actionTypeLabel,
  "approval_binding.used": (v) => (v ? QA_YES : QA_NO),
  "approval_binding.revoked": (v) => (v ? QA_YES : QA_NO),
  "approval_binding.risk_level": riskGradeLabel,
  "execution_result.action_type": actionTypeLabel,
  "execution_result.executed": (v) => (v ? QA_YES : QA_NO),
  "execution_result.success": (v) => (v ? QA_YES : QA_NO),
  "closure_state.ticket_status": ticketStatusLabel,
  "closure_state.ticket_settled": (v) => (v ? QA_YES : QA_NO),
  "closure_state.analysis_degraded": (v) => (v ? QA_YES : QA_NO),
};

// These carry another producer's own words (a policy message, an executor message) and are echoed
// verbatim in a monospace run. They are never put through a word map: they are not this repo's
// vocabulary to translate, and paraphrasing an executor's own error text would be a lie.
const ECHO_AS_AUTHORED = new Set(["tool_entitlement.policy_reason", "execution_result.message"]);

// Identifiers the operator may want but this projection cannot resolve to a name: it is read-only
// over the run's own rows and does not join the users table. Labelled as identifiers above so a
// reader never takes one for a display name.
const ECHO_AS_IDENTIFIER = new Set([
  "employee_scope.agent_client_id",
  "employee_scope.ticket_company_id",
  "approval_binding.approver_id",
]);

function plain(value) {
  if (value === null || value === undefined) return QA_ABSENT;
  if (typeof value === "boolean") return value ? QA_YES : QA_NO;
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim() || QA_ABSENT;
  return null;
}

function list(items, word) {
  return items
    .map((item) => (typeof item === "string" && item.trim() ? word(item) : plain(item)))
    .filter(Boolean)
    .join(" · ") || QA_ABSENT;
}

/**
 * A slot field as the operator reads it. Returns `{ text, mono }`: `mono` marks a value that is the
 * producer's own token or message and must be rendered left-to-right in a monospace run.
 */
export function qaFieldValue(slotKind, key, value) {
  const id = `${slotKind}.${key}`;

  // Checked before every worder: a boolean worder turns `false` into "לא" and would turn an ABSENT
  // value into "לא" too, which is not an absence — it is a claim the server never made. `executed:
  // null` means the record does not say; `executed: false` means it says no.
  if (value === null || value === undefined) return { text: QA_ABSENT, mono: false };

  if (ECHO_AS_AUTHORED.has(id)) {
    const text = plain(value);
    return { text: text === null ? QA_ABSENT : text, mono: true };
  }
  if (ECHO_AS_IDENTIFIER.has(id)) {
    const text = plain(value);
    // An identifier is echoed in full: a truncated UUID is not a shorter identifier, it is a
    // different one. It is monospace and left-to-right, so it reads as an identifier.
    return { text: text === null ? QA_ABSENT : text, mono: true };
  }

  if (id === "declared_evidence.evidence_sources") {
    const items = Array.isArray(value) ? value : [];
    if (!items.length) return { text: "לא הוצהר אף מקור", mono: false };
    return { text: list(items, evidenceSourceLabel), mono: false };
  }
  if (id === "proposed_action.analysis_action_types") {
    return { text: list(Array.isArray(value) ? value : [], actionTypeLabel), mono: false };
  }
  if (id === "execution_result.action_outcomes") {
    const entries = value && typeof value === "object" ? Object.entries(value) : [];
    if (!entries.length) return { text: QA_ABSENT, mono: false };
    return { text: entries.map(([k, n]) => `${qaActionOutcomeLabel(k)} ×${n}`).join(" · "), mono: false };
  }

  const word = Object.prototype.hasOwnProperty.call(VALUE_WORDERS, id) ? VALUE_WORDERS[id] : null;
  if (word) {
    const text = word(value);
    return { text: text === null || text === undefined || text === "" ? QA_ABSENT : text, mono: false };
  }

  const direct = plain(value);
  if (direct !== null) return { text: direct, mono: false };
  // A shape this build does not know (an object it has no worder for) is echoed as JSON rather than
  // summarised: a summary would be this module's own reading of someone else's payload.
  try {
    return { text: JSON.stringify(value), mono: true };
  } catch {
    return { text: QA_ABSENT, mono: true };
  }
}

// ── authority ────────────────────────────────────────────────────────────────

export const QA_AUTHORITY_NOTE =
  "משטח ראיות בלבד: הסיווג אינו מעניק הרצה, אינו מאשר פעולה, אינו משנה את מצב הקריאה ואינו עוקף מדיניות — "
  + "אוטונומיה נקבעת לפי מדיניות המערכת ולא לפי המסך הזה.";

// The server sends every authority flag as False and a test asserts it. If one ever comes back true,
// the operator must read that instead of the reassuring note: a projection that started granting
// something is not a projection any more.
export const QA_AUTHORITY_ALERT =
  "אזהרה: השרת דיווח שהמשטח הזה אינו קריאה בלבד. אין לפעול לפי המסך הזה — יש לדווח על כך.";

export function qaAuthorityNotice(authority) {
  const a = authority && typeof authority === "object" ? authority : null;
  if (!a) return { text: QA_AUTHORITY_NOTE, alarm: false };
  const alarming = a.read_only !== true
    || a.grants_execution === true
    || a.grants_approval === true
    || a.changes_ticket_state === true
    || a.overrides_policy === true;
  return alarming ? { text: QA_AUTHORITY_ALERT, alarm: true } : { text: QA_AUTHORITY_NOTE, alarm: false };
}

// ── the analysis-scale evidence sources ──────────────────────────────────────

/**
 * The Hebrew name of one `analysis.evidence_sources` token.
 *
 * Like `actionTypeLabel`, an unmapped token is returned as itself: this field's own parity test
 * (`evidenceSourceVocabulary.test.jsx`) holds `EVIDENCE_SOURCE_LABEL` equal to the producer's closed
 * set, so this branch is a token nobody has swept yet rather than a normal case — and showing the
 * producer's identifier is the honest failure. Blanking the row or inventing a word would both hide
 * the fact that the analysis cited something this build cannot name.
 */
export const evidenceSourceLabel = (token) =>
  Object.prototype.hasOwnProperty.call(EVIDENCE_SOURCE_LABEL, token) ? EVIDENCE_SOURCE_LABEL[token] : token;
